<!-- TO DO: open select above button + portal (@headlessui-float/vue) -->
<script setup lang="ts">
export interface SelectOption {
  id: number
  text: string
  value: string | number | Date
  inactive?: boolean
}

export interface SelectProps {
  options: SelectOption[]
  defaultValue?: SelectOption
  modelValue?: SelectOption
  label?: string
  labelClasses?: unknown
  hideLabel?: boolean
  orientation?: 'vertical' | 'horizontal'
  variant?: 'primary' | 'secondary' | 'success' | 'error' | 'neutral'
  warning?: string
  placeholder?: string
  dropdownHeight?: string
  buttonClasses?: string
}

const internalDefaultValue = {
  id: null,
  text: 'Select',
  value: null,
}

const emits = defineEmits<{
  (e: 'update:modelValue', value: SelectOption): void
}>()

const props = withDefaults(defineProps<SelectProps>(), {
  orientation: 'vertical',
  variant: 'primary',
  dropdownHeight: 'max-h-60',
})
</script>

<template>
  <HuiListbox
    v-slot="{ open }"
    as="div"
    :class="[
      'flex w-full gap-2',
      {
        'flex-row items-center': props.orientation === 'horizontal',
        'flex-col': props.orientation === 'vertical',
      },
    ]"
    :default-value="props.defaultValue ?? internalDefaultValue"
    :disabled="!props.modelValue"
    :model-value="props.modelValue"
    @update:model-value="
      (value: SelectOption) => emits('update:modelValue', value)
    "
  >
    <HuiListboxLabel
      v-if="label"
      :class="[
        'flex w-full text-xs uppercase text-[#8B90A5]',
        {
          'sr-only': props.hideLabel,
        },
        props.labelClasses,
      ]"
    >
      {{ label }}
    </HuiListboxLabel>

    <div class="relative w-full">
      <HuiListboxButton
        v-slot="{ value }"
        :class="[
          'relative min-h-[2.6rem] w-full cursor-default rounded-md border py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 ui-disabled:cursor-not-allowed ui-disabled:opacity-60 flex items-center gap-2',
          {
            'focus:border-primary-500 focus:ring-primary-500':
              props.variant === 'primary',
            'focus:border-gray-500 focus:ring-gray-500':
              props.variant === 'secondary',
            'focus:border-emerald-500 focus:ring-emerald-500':
              props.variant === 'success',
            'border-brand-neutral bg-brand-neutral text-slate-600 focus:border-[#A8C8F8] focus:ring-[#A8C8F8]':
              props.variant === 'neutral',
          },
          props.buttonClasses,
        ]"
      >
        <slot name="icon"/>
        <span
          class="block truncate"
          :class="{ 'text-[#8B90A5]': value.text == '' }"
        >
          {{ value.text != '' ? value.text : props.placeholder }}
        </span>

        <span
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <HeroiconsChevronDown
            class="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </HuiListboxButton>

      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <HuiListboxOptions
          :class="[
            'absolute mt-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            props.dropdownHeight,
            { 'z-10': open },
          ]"
        >
          <HuiListboxOption
            v-for="option in options"
            v-slot="{ active, selected }"
            :key="option.id"
            as="template"
            :value="option"
            :disabled="option.inactive"
          >
            <li
              :class="[
                active
                  ? {
                      'bg-primary-500 text-white': props.variant === 'primary',
                      'bg-gray-600 text-white': props.variant === 'secondary',
                      'bg-emerald-600 text-white': props.variant === 'success',
                      'bg-brand-neutral text-slate-800':
                        props.variant === 'neutral',
                    }
                  : {
                      'text-gray-900': [
                        'primary',
                        'secondary',
                        'success',
                      ].includes(props.variant),
                    },
                'relative cursor-default select-none py-2 pl-10 pr-4',
                {
                  'opacity-50': option.inactive,
                },
              ]"
            >
              <span
                :class="[
                  selected ? 'font-medium' : 'font-normal',
                  'block truncate',
                ]"
              >
                {{ option.text }}
              </span>

              <span
                v-if="selected"
                :class="[
                  active ? 'text-slate-800' : 'text-gray-900',
                  'absolute inset-y-0 left-0 flex items-center pl-3',
                ]"
              >
                <HeroiconsCheck class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </HuiListboxOption>
        </HuiListboxOptions>
      </Transition>

      <p v-show="warning" class="mt-1 text-sm text-red-700">
        {{ warning }}
      </p>
    </div>
  </HuiListbox>
</template>
